import { Resolver, Mutation, Arg, Ctx, Directive } from 'type-graphql'
import { In } from 'typeorm'
import { getRepository } from '@things-factory/shell'

import { createAttachment, deleteAttachmentsByRef } from '@things-factory/attachment-base'
import { Kpi } from './kpi'
import { NewKpi, KpiPatch } from './kpi-type'
import { KpiHistory } from './kpi-history'
import { KpiFormulaService } from './kpi-formula.service'
import { Application, CallbackBase, registerSchedule, unregisterSchedule } from '@things-factory/scheduler-client'
import { KpiValue } from '../kpi-value/kpi-value'
import { KpiOrgScope } from '../kpi-org-scope/kpi-org-scope'
import { Between } from 'typeorm'
import { KpiMetric } from '../kpi-metric/kpi-metric'
import { KpiMetricValue } from '../kpi-metric-value/kpi-metric-value'
import { parseFormula } from '../../calculator/parser'
import { evaluateFormula } from '../../calculator/evaluator'
import { builtinFunctions } from '../../calculator/functions'
import { KpiMetricValueProvider } from '../../controllers/kpi-metric-value-provider'

@Resolver(Kpi)
export class KpiMutation {
  @Directive('@transaction')
  @Directive('@privilege(category: "kpi", privilege: "mutation", domainOwnerGranted: true, superUserGranted: true)')
  @Mutation(returns => Kpi, { description: 'Create a new KPI with the provided details.' })
  async createKpi(
    @Arg('kpi', { description: 'Input object containing details for the new KPI.' }) kpi: NewKpi,
    @Ctx() context: ResolverContext
  ): Promise<Kpi> {
    const { domain, user, tx } = context.state

    let parent = kpi.parent
      ? await getRepository(Kpi).findOne({ where: { id: kpi.parent.id, domain: { id: domain.id } } })
      : undefined

    // 순환 참조 검사
    if (parent) {
      const formulaService = new KpiFormulaService()
      const hasCircular = await formulaService.hasCircularReference(parent.id, parent.id)
      if (hasCircular) {
        throw new Error('Circular reference detected in KPI hierarchy')
      }
    }

    const result = await getRepository(Kpi, tx).save({
      ...kpi,
      parent,
      domain,
      creator: user,
      updater: user
    })

    if (kpi.thumbnail) {
      await createAttachment(
        null,
        {
          attachment: {
            file: kpi.thumbnail,
            refType: Kpi.name,
            refBy: result.id
          }
        },
        context
      )
    }

    if (kpi.formula) {
      const formulaService = new KpiFormulaService()
      const result = await formulaService.validateFormula(kpi.formula)
      if (result.warnings?.length) {
        console.warn(`[KPI formula 경고] ${kpi.name}:\n` + result.warnings.join('\n'))
      }
      if (!result.valid) {
        throw new Error(result.errors.join('\n'))
      }
    }

    // KPI 스케줄러 등록
    if (kpi.schedule) {
      const handle = await registerSchedule({
        name: result.name,
        client: {
          application: Application,
          group: `${domain.id}`,
          type: 'kpi',
          key: result.id,
          operation: 'schedule'
        },
        type: 'cron',
        schedule: kpi.schedule,
        timezone: kpi.timezone,
        task: {
          type: 'rest',
          connection: {
            host: `${CallbackBase}/callback-schedule-for-kpi`,
            headers: {
              'Content-Type': 'application/json',
              accept: '*/*'
            }
          },
          data: {
            domainId: domain.id,
            kpiId: result.id
          },
          history_check: true,
          failed_policy: 'retry_dlq',
          max_retry_count: 3,
          retry_period: 60
        }
      })
      result.scheduleId = handle
      await getRepository(Kpi, tx).save(result)
    }

    return result
  }

  @Directive('@transaction')
  @Directive('@privilege(category: "kpi", privilege: "mutation", domainOwnerGranted: true, superUserGranted: true)')
  @Mutation(returns => Kpi, { description: 'To modify Kpi information' })
  async updateKpi(@Arg('id') id: string, @Arg('patch') patch: KpiPatch, @Ctx() context: ResolverContext): Promise<Kpi> {
    const { domain, user, tx } = context.state

    const repository = getRepository(Kpi, tx)
    const kpi = await repository.findOne({
      where: { domain: { id: domain.id }, id }
    })

    let parent = patch.parent
      ? await getRepository(Kpi).findOne({ where: { id: patch.parent.id, domain: { id: domain.id } } })
      : patch.parent === null ? null : kpi.parent

    // 순환 참조 검사 (parent가 변경되는 경우)
    if (parent && parent.id !== kpi.parentId) {
      const formulaService = new KpiFormulaService()
      const hasCircular = await formulaService.hasCircularReference(kpi.id, parent.id)
      if (hasCircular) {
        throw new Error('Circular reference detected in KPI hierarchy')
      }
    }

    const result = await repository.save({
      ...kpi,
      ...patch,
      parent,
      updater: user
    })

    if (patch.thumbnail) {
      await deleteAttachmentsByRef(null, { refBys: [result.id] }, context)
      await createAttachment(
        null,
        {
          attachment: {
            file: patch.thumbnail,
            refType: Kpi.name,
            refBy: result.id
          }
        },
        context
      )
    }

    if (patch.formula) {
      const formulaService = new KpiFormulaService()
      const result = await formulaService.validateFormula(patch.formula)
      if (result.warnings?.length) {
        console.warn(`[KPI formula 경고] ${patch.name || patch.id}:\n` + result.warnings.join('\n'))
      }
      if (!result.valid) {
        throw new Error(result.errors.join('\n'))
      }
    }

    // KPI 스케줄러 등록/해제 갱신
    if (patch.schedule) {
      // 기존 스케줄러 해제
      if (kpi.scheduleId) {
        await unregisterSchedule(kpi.scheduleId)
      }
      // 새 스케줄러 등록
      const handle = await registerSchedule({
        name: result.name,
        client: {
          application: Application,
          group: `${domain.id}`,
          type: 'kpi',
          key: result.id,
          operation: 'schedule'
        },
        type: 'cron',
        schedule: patch.schedule,
        timezone: patch.timezone,
        task: {
          type: 'rest',
          connection: {
            host: `${CallbackBase}/callback-schedule-for-kpi`,
            headers: {
              'Content-Type': 'application/json',
              accept: '*/*'
            }
          },
          data: {
            domainId: domain.id,
            kpiId: result.id
          },
          history_check: true,
          failed_policy: 'retry_dlq',
          max_retry_count: 3,
          retry_period: 60
        }
      })
      result.scheduleId = handle
      await repository.save(result)
    } else if (patch.schedule === null && kpi.scheduleId) {
      // 스케줄 해제 요청
      await unregisterSchedule(kpi.scheduleId)
      result.scheduleId = null
      await repository.save(result)
    }

    return result
  }

  @Directive('@transaction')
  @Directive('@privilege(category: "kpi", privilege: "mutation", domainOwnerGranted: true, superUserGranted: true)')
  @Mutation(returns => [Kpi], { description: "To modify multiple Kpis' information" })
  async updateMultipleKpi(
    @Arg('patches', type => [KpiPatch]) patches: KpiPatch[],
    @Ctx() context: ResolverContext
  ): Promise<Kpi[]> {
    const { domain, user, tx } = context.state

    let results = []
    const _createRecords = patches.filter((patch: any) => patch.cuFlag.toUpperCase() === '+')
    const _updateRecords = patches.filter((patch: any) => patch.cuFlag.toUpperCase() === 'M')
    const kpiRepo = getRepository(Kpi, tx)

    if (_createRecords.length > 0) {
      for (let i = 0; i < _createRecords.length; i++) {
        const newRecord = _createRecords[i]

        const result = await kpiRepo.save({
          ...newRecord,
          domain,
          creator: user,
          updater: user
        })

        if (newRecord.thumbnail) {
          await createAttachment(
            null,
            {
              attachment: {
                file: newRecord.thumbnail,
                refType: Kpi.name,
                refBy: result.id
              }
            },
            context
          )
        }

        results.push({ ...result, cuFlag: '+' })
      }
    }

    if (_updateRecords.length > 0) {
      for (let i = 0; i < _updateRecords.length; i++) {
        const updateRecord = _updateRecords[i]
        const kpi = await kpiRepo.findOne({
          where: { domain: { id: domain.id }, id: updateRecord.id }
        })

        if (!kpi) {
          throw new Error(`failed to find kpi to update: ${updateRecord.id}`)
        }

        const result = await kpiRepo.save({
          ...kpi,
          ...updateRecord,
          updater: user
        })

        if (updateRecord.thumbnail) {
          await deleteAttachmentsByRef(null, { refBys: [result.id] }, context)
          await createAttachment(
            null,
            {
              attachment: {
                file: updateRecord.thumbnail,
                refType: Kpi.name,
                refBy: result.id
              }
            },
            context
          )
        }

        results.push({ ...result, cuFlag: 'M' })
      }
    }

    return results
  }

  @Directive('@transaction')
  @Directive('@privilege(category: "kpi", privilege: "mutation", domainOwnerGranted: true, superUserGranted: true)')
  @Mutation(returns => Boolean, { description: 'To delete Kpi' })
  async deleteKpi(@Arg('id') id: string, @Ctx() context: ResolverContext): Promise<boolean> {
    const { domain, tx } = context.state

    const kpi = await getRepository(Kpi, tx).findOne({ where: { domain: { id: domain.id }, id } })
    if (kpi && kpi.scheduleId) {
      await unregisterSchedule(kpi.scheduleId)
    }

    await getRepository(Kpi, tx).delete({ domain: { id: domain.id }, id })
    await deleteAttachmentsByRef(null, { refBys: [id] }, context)

    return true
  }

  @Directive('@transaction')
  @Directive('@privilege(category: "kpi", privilege: "mutation", domainOwnerGranted: true, superUserGranted: true)')
  @Mutation(returns => Boolean, { description: 'To delete multiple Kpis' })
  async deleteKpis(@Arg('ids', type => [String]) ids: string[], @Ctx() context: ResolverContext): Promise<boolean> {
    const { domain, tx } = context.state

    await getRepository(Kpi, tx).delete({
      domain: { id: domain.id },
      id: In(ids)
    })

    await deleteAttachmentsByRef(null, { refBys: ids }, context)

    return true
  }

  @Directive('@transaction')
  @Directive('@privilege(category: "kpi", privilege: "mutation", domainOwnerGranted: true, superUserGranted: true)')
  @Mutation(returns => Boolean, { description: 'To import multiple Kpis' })
  async importKpis(
    @Arg('kpis', type => [KpiPatch]) kpis: KpiPatch[],
    @Ctx() context: ResolverContext
  ): Promise<boolean> {
    const { domain, tx } = context.state

    await Promise.all(
      kpis.map(async (kpi: KpiPatch) => {
        const createdKpi: Kpi = await getRepository(Kpi, tx).save({ domain, ...kpi })
      })
    )

    return true
  }

  @Directive('@transaction')
  @Directive('@privilege(category: "kpi", privilege: "mutation", domainOwnerGranted: true, superUserGranted: true)')
  @Mutation(returns => Kpi, { description: 'Release a KPI and create a version history.' })
  async releaseKpi(@Arg('id') id: string, @Ctx() context: ResolverContext): Promise<Kpi> {
    const { domain, user, tx } = context.state
    const repository = getRepository(Kpi, tx)
    const historyRepository = getRepository(KpiHistory, tx)

    const kpi = await repository.findOne({
      where: { domain: { id: domain.id }, id }
    })
    if (!kpi) throw `KPI given id(${id}) is not found`
    if (kpi.state == 'RELEASE') throw `KPI given id(${id}) is already released`

    // 히스토리에서 max version 가져오기
    const maxHistory = await historyRepository
      .createQueryBuilder('history')
      .select('MAX(history.version)', 'max')
      .where('history.originalId = :id', { id: kpi.id })
      .getRawOne()
    const nextVersion = maxHistory?.max ? Number(maxHistory.max) + 1 : 1

    const updated = await repository.save({
      ...kpi,
      version: nextVersion,
      state: 'RELEASE',
      updater: user
    } as any)
    return updated
  }

  @Directive('@transaction')
  @Directive('@privilege(category: "kpi", privilege: "mutation", domainOwnerGranted: true, superUserGranted: true)')
  @Mutation(returns => Kpi, { description: 'Revert a KPI to a specific historical version.' })
  async revertKpiVersion(
    @Arg('id') id: string,
    @Arg('version') version: number,
    @Ctx() context: ResolverContext
  ): Promise<Kpi> {
    const { domain, user, tx } = context.state
    const repository = getRepository(Kpi, tx)
    const historyRepository = getRepository(KpiHistory, tx)

    const kpi = await repository.findOne({
      where: { domain: { id: domain.id }, id }
    })
    if (!kpi) throw `KPI with id(${id}) is not found`

    const kpiHistory = await historyRepository.findOne({
      where: { domain: { id: domain.id }, originalId: id, version },
      order: { version: 'DESC' }
    })
    if (!kpiHistory) throw `KPI with id:version(${id}:${version}) is not found`

    const updated = await repository.save({
      ...kpi,
      name: kpiHistory.name,
      description: kpiHistory.description,
      formula: kpiHistory.formula,
      active: kpiHistory.active,
      state: 'DRAFT',
      thumbnail: kpiHistory.thumbnail,
      updater: user
    } as any)
    return updated
  }

  @Directive('@transaction')
  @Directive('@privilege(category: "kpi", privilege: "mutation", domainOwnerGranted: true, superUserGranted: true)')
  @Mutation(returns => KpiValue, { description: 'KPI 기준으로 formula 계산 및 KPI Value upsert' })
  async calculateKpiValue(
    @Arg('kpiId') kpiId: string,
    @Arg('valueDate', { nullable: true }) valueDate: string,
    @Arg('org', { nullable: true }) org: string,
    @Ctx() context: ResolverContext
  ): Promise<KpiValue> {
    const { domain, user, tx } = context.state
    const kpiRepo = getRepository(Kpi, tx)
    const metricRepo = getRepository<KpiMetric>(require('../kpi-metric/kpi-metric').KpiMetric, tx)
    const metricValueRepo = getRepository<KpiMetricValue>(
      require('../kpi-metric-value/kpi-metric-value').KpiMetricValue,
      tx
    )
    const kpiValueRepo = getRepository<KpiValue>(KpiValue, tx)
    const { KpiPeriodType } = require('./kpi')
    const { getDefaultValueDate } = require('../utils/value-date-util')

    // 1. KPI 정보 조회
    const kpi = await kpiRepo.findOne({ where: { id: kpiId, domain: { id: domain.id } } })
    if (!kpi) throw new Error('KPI 정보 없음')
    if (!kpi.formula) throw new Error('KPI formula 없음')
    const periodType = kpi.periodType || KpiPeriodType.DAY
    const valueDateToUse = valueDate || getDefaultValueDate(periodType, 'last')

    // 2. formula에서 metric code 추출
    const metricCodes = (kpi.formula.match(/[a-zA-Z_][a-zA-Z0-9_]*/g) || []).filter(
      code => code !== 'null' && code !== 'undefined'
    )
    const metricMap: Record<string, any> = {}
    for (const code of metricCodes) {
      const metric = await metricRepo.findOne({ where: { name: code, domain: { id: domain.id } } })
      if (!metric) throw new Error(`KPI formula metric '${code}' not found`)
      metricMap[code] = metric
    }

    // 3. metric 값 집계 (periodType 변환/집계)
    const metricValues: Record<string, number> = {}
    for (const code of metricCodes) {
      const metric = metricMap[code]
      let value = null
      if (metric.periodType === periodType) {
        const mv = await metricValueRepo.findOne({
          where: {
            metric: { id: metric.id },
            valueDate: valueDateToUse,
            periodType,
            org: org ?? '',
            domain: { id: domain.id }
          }
        })
        value = mv?.value ?? null
      } else {
        let startDate: string, endDate: string
        if (periodType === KpiPeriodType.MONTH && metric.periodType === KpiPeriodType.DAY) {
          startDate = valueDateToUse + '-01'
          endDate = valueDateToUse + '-31'
          const mvs = await metricValueRepo.find({
            where: {
              metric: { id: metric.id },
              valueDate: Between(startDate, endDate),
              periodType: metric.periodType,
              org: org ?? '',
              domain: { id: domain.id }
            }
          })
          value = mvs.reduce((sum, mv) => sum + (mv.value ?? 0), 0)
        } else {
          throw new Error('KPI/Metric periodType 조합 집계 미지원')
        }
      }
      if (value == null) throw new Error(`Metric '${code}' 값 없음`)
      metricValues[code] = value
    }

    // 4. formula 계산 (calculator 기반)
    const ast = parseFormula(kpi.formula)
    const provider = new KpiMetricValueProvider({
      valueDate: valueDateToUse,
      org,
      domainId: domain.id,
      tx
    })
    const evalContext = { functions: builtinFunctions, provider }
    const kpiValueResult = await evaluateFormula(ast, evalContext)

    // 5. KpiOrgScope 처리
    let kpiOrgScope: KpiOrgScope | null = null
    if (org) {
      kpiOrgScope = await getRepository(KpiOrgScope, tx).findOne({
        where: { org: org, domain: { id: domain.id } }
      })
      if (!kpiOrgScope) {
        // 새 KpiOrgScope 생성
        kpiOrgScope = await getRepository(KpiOrgScope, tx).save({
          entityType: 'KpiCalculation',
          entityId: `calc-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          entityName: org,
          org: org,
          domain,
          creator: user,
          updater: user
        })
      }
    }

    // 6. KPI Value upsert
    let kpiValue = await kpiValueRepo.findOne({
      where: {
        kpi: { id: kpi.id },
        valueDate: valueDateToUse,
        kpiOrgScope: kpiOrgScope ? { id: kpiOrgScope.id } : null,
        version: kpi.version,
        domain: { id: domain.id }
      }
    })
    if (kpiValue) {
      kpiValue.value = kpiValueResult
      kpiValue.updater = user
    } else {
      const createData: any = {
        kpi,
        kpiId: kpi.id,
        version: kpi.version,
        valueDate: valueDateToUse,
        value: kpiValueResult,
        domain,
        creator: user,
        updater: user
      }
      if (kpiOrgScope) {
        createData.kpiOrgScope = kpiOrgScope
        createData.kpiOrgScopeId = kpiOrgScope.id
      }
      kpiValue = Object.assign(new KpiValue(), createData)
    }
    return await kpiValueRepo.save(kpiValue)
  }
}
